import React, { useEffect } from 'react'
import MicroModal from "micromodal"

import { EnrolledCoursesData, CoursesEnrolledReportData } from './data/EnrolledCoursesData'
import {
  ColumnDef,
  SortingState
} from '@tanstack/react-table'

import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import { useUserReport } from '../context/UserReportContext'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { getExportHeaders } from '../types/tableHeaders'
import Button from '../../../../common/button/Button'

const EnrolledCourses = ({ courses}) => {
  const { selectedCourse, setSelectedCourse, selectedUser } = useUserReport()

  const [rowSelection, setRowSelection] = React.useState({})
  const [globalFilter, setGlobalFilter] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [data, setData] = React.useState(() => CoursesEnrolledReportData( courses, selectedUser ) )
  
  useEffect(()=>{
    setData(()=> CoursesEnrolledReportData( courses, selectedUser ))
  }, [ selectedCourse, selectedUser ])
  
  MicroModal.init()

  const columns = React.useMemo<ColumnDef<EnrolledCoursesData>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'courseId',
            accessorKey: 'courseId',
            prettyName: 'Course ID',
            show: false,
            cell: info => info.getValue(),
            header: () => <span>COURSE ID</span>,
          },
          {
            id: 'course',
            accessorKey: 'course',
            prettyName: 'Course',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>COURSE</span>,
          },
          { 
            id: 'completePercentage',
            accessorKey: 'completePercentage',
            prettyName: 'Complete',
            cell: info => info.getValue() + "%",
            footer: props => props.column.id,
            header: () => <span>COMPLETE</span>,
          },
          { 
            id: 'dateCompleted',
            accessorKey: 'dateCompleted',
            prettyName: 'Date Completed',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>DATE COMPLETED</span>,
          },
          { 
            id: 'avgQuizScore',
            accessorKey: 'avgQuizScore',
            prettyName: 'AVG Quiz Score',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>AVG QUIZ SCORE</span>,
          },
          { 
            id: 'viewEnrolledDetails',
            accessorKey: 'viewEnrolledDetail',
            prettyName: 'Details',
            cell: (info) => (
              <ViewDetails info={info.row.original} />
            ),
            header: () => <span>DETAILS</span>,
          },
        ],
      },
    ],
    []
  )

  const exportHeaders = getExportHeaders( 'enrolled-courses' )

  const handleThisModal = (info) => {
    MicroModal.close('msync-report-user-course-list')
    MicroModal.show('msync-report-user-container')

    const progress = selectedUser.progress.find(p => Number(p.course_id) === Number( info.courseId ))
    const steps = progress?.steps_progress ? progress.steps_progress : {}

    selectedUser.steps = steps

    setSelectedCourse(info)
  }

  const ViewDetails = ({info}) => {
    return (
      <Button 
        name='View Details'
        className='msync-button--is-size-sm msync-button--is-theme-secondary msync-button--is-style-outline' 
        onClick={() => handleThisModal(info)}
      />
    )
  }

  return (
    <TanstackContent
      data={data}
      columns={columns}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      sorting={sorting}
      setSorting={setSorting}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      exportHeaders={exportHeaders}
      from={'Enrolled Courses'}
      context={'Users'}
      children={null}
      columnFilters={ null }
      setColumnFilters={ null }
    />
  )
}

export default EnrolledCourses
